import { type FC, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCreateClientEvent, useGetClientEvents } from '@/hooks';
import { CardType } from '@/types';
import { Typography } from '@/ui';

import { useMemberSessionReflectionsHome } from './SessionReflections/useMemberSessionReflectionsHome';
import { DISMISS_FOREVER_TILL, isDismissed } from './bannerDismissal';
import bannerBackground from './moments-intro-banner-bg.png';
import reflectionPreview from './moments-intro-reflection-preview.png';

import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';

export interface MomentsIntroBannerPureProps {
  /** Route to the member's most recent session reflection */
  latestReflectionHref: string;
  onDismiss: () => void;
  onCtaClick?: () => void;
}

export const MomentsIntroBannerPure: FC<MomentsIntroBannerPureProps> = ({
  latestReflectionHref,
  onDismiss,
  onCtaClick,
}) => (
  // max-w-[832px] matches NextSessionPrepCard and SessionReflectionsModule so
  // the banner lines up with the Moments module underneath it, as in Figma.
  <section className="mb-6 w-full max-w-[832px] px-4 md:px-0">
    {/* bg-sage-600 matches the aurora's dark green so the card still reads as
        a card (and the white copy stays legible) before the background PNG
        loads. */}
    <div className="relative overflow-hidden rounded-2xl bg-sage-600">
      <img
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 size-full object-cover"
        src={bannerBackground}
      />

      {/* 352 - 40 left padding = a 312px text measure, which is what Figma
          lays the copy out to. Widening it changes where the headline breaks. */}
      <div className="relative z-10 flex min-h-[365px] flex-col justify-center p-6 md:w-[352px] md:py-10 md:pl-10 md:pr-0">
        {/* The headline gets a narrower measure than the body copy, as in
            Figma, so it breaks as "See what your / sessions are / really
            about". At the body's 312px, "really" pulls up onto line two. */}
        <Typography
          className="mb-4 max-w-[260px] font-bold leading-tight text-white"
          variant="h4"
        >
          See what your sessions are really about
        </Typography>
        <Typography className="mb-6 text-white/90" variant="body1">
          Introducing Moments. Short reads before and after each session that
          get at the meaning underneath the conversation, not just what was
          said.
        </Typography>

        {/* Both controls use Figma's `components/button-medium` token: 13px
            bold, 24px line-height, 0.4px tracking. With py-1.5/px-4 that
            renders the pill at 232x36, matching the mock and the shared MUI
            Button's medium size. md:flex-nowrap keeps Dismiss beside the CTA
            rather than wrapping it onto its own line. */}
        <div className="flex flex-wrap items-center gap-4 md:flex-nowrap">
          <Link
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-white px-4 py-1.5 text-center text-[13px] font-bold leading-6 tracking-[0.4px] text-cement-600 no-underline hover:bg-cement-100"
            to={latestReflectionHref}
            onClick={onCtaClick}
          >
            See my latest session reflection
          </Link>
          <button
            className="whitespace-nowrap border-none bg-transparent p-0 text-[13px] font-bold leading-6 tracking-[0.4px] text-white underline-offset-2 hover:underline"
            type="button"
            onClick={onDismiss}
          >
            Dismiss
          </button>
        </div>
      </div>

      {/* The preview PNG is a 2x export (890x678), so it is drawn at its
          natural 445px and anchored to the bottom-right, where its baked-in
          rounded corners and transparent margin do the framing. Anything
          width-relative blows it up to several times its intended size.

          The img intentionally has no src: with no matching <source>, no image
          is fetched at all, so phones never download a preview the `hidden`
          class would hide anyway. The media query must track the `md`
          breakpoint in tailwind.config.cjs (900px, not Tailwind's 768px
          default), or sizes in between pay for a hidden image. */}
      <picture>
        <source media="(min-width: 900px)" srcSet={reflectionPreview} />
        <img
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 right-2 hidden w-[445px] max-w-none md:block"
        />
      </picture>
    </div>
  </section>
);

const MomentsIntroBanner: FC = () => {
  const { data: clientEvents } = useGetClientEvents();
  const createClientEvent = useCreateClientEvent();
  const { rows } = useMemberSessionReflectionsHome();
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  const dismissed = useMemo(
    () =>
      isDismissed(
        (clientEvents || []).find(
          (event) => event.key === CardType.MEMBER_MOMENTS_BANNER,
        )?.dismissedTill,
      ),
    [clientEvents],
  );
  const latest = rows[0];
  const isVisible =
    !locallyDismissed &&
    clientEvents !== undefined &&
    !dismissed &&
    !!latest;

  useEffect(() => {
    if (!isVisible) return;

    const route = window.location.pathname;
    momentsAnalytics.viewed(
      MEMBER_MOMENTS_EVENTS.viewedMomentsIntroBanner,
      'member_home',
      route,
      buildMemberMomentsAnalyticsProperties({
        entrypoint: 'moments_intro_banner',
        route,
        surface: 'home',
      }),
      { dedupeKey: 'moments-intro-banner' },
    );
  }, [isVisible]);

  if (!isVisible) return null;

  const handleCtaClick = () => {
    const route = window.location.pathname;
    momentsAnalytics.track(
      MEMBER_MOMENTS_EVENTS.clickedMomentsIntroBannerCta,
      buildMemberMomentsAnalyticsProperties({
        artifactId: latest.id,
        destination: latest.href,
        entrypoint: 'moments_intro_banner',
        route,
        surface: 'home',
      }),
    );
  };

  const handleDismiss = () => {
    setLocallyDismissed(true);
    const route = window.location.pathname;
    momentsAnalytics.track(
      MEMBER_MOMENTS_EVENTS.dismissedMomentsIntroBanner,
      buildMemberMomentsAnalyticsProperties({
        entrypoint: 'moments_intro_banner',
        route,
        surface: 'home',
      }),
    );
    createClientEvent.mutate({
      dismissedTill: DISMISS_FOREVER_TILL,
      key: CardType.MEMBER_MOMENTS_BANNER,
    });
  };

  return (
    <MomentsIntroBannerPure
      latestReflectionHref={latest.href}
      onCtaClick={handleCtaClick}
      onDismiss={handleDismiss}
    />
  );
};

export default MomentsIntroBanner;
